import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BillingAuditTimeEntry,
  BulkSendToReviewInput,
  BulkSendToReviewResult,
  BulkSetInvoiceDateInput,
  BulkSetInvoiceDateResult,
  CreateInvoiceInput,
  CreateInvoiceLineInput,
  GenerateInvoicesInput,
  Invoice,
  InvoiceGenerationSource,
  InvoicePreview,
  InvoiceReviewerOption,
  InvoiceSource,
  InvoiceStatus,
  InvoiceTotals,
  PaginatedResult,
  ProrateInvoicePreview,
  GenerateProrateInput,
  QboBatchActionInput,
  QboBatchResult,
  QboSyncResult,
  RecomputeRatesResult,
  RefreshQboMappingResult,
  RequestInvoiceChangesInput,
  SendInvoiceInput,
  SendToReviewInput,
  SortDirection,
  UpdateInvoiceInput,
  UpdateInvoiceLineInput,
  VoidInvoiceInput,
  WriteOffTimeEntryInput,
} from '@catalyst-psa/shared';
import { api, type DownloadResult } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface InvoiceListParams {
  page: number;
  pageSize: number;
  /** Free-text search: matches customer name or any portion of the invoice number. */
  search?: string;
  customerId?: string;
  agreementId?: string;
  projectId?: string;
  activeReviewerId?: string;
  /** Comma-separated reviewer member ids (matches any). */
  activeReviewerIds?: string;
  source?: InvoiceSource;
  status?: InvoiceStatus;
  /** Match any of these statuses (sent as a comma-separated list). */
  statuses?: InvoiceStatus[];
  /** Earliest invoice date (yyyy-mm-dd). */
  issueDateFrom?: string;
  /** Latest invoice date (yyyy-mm-dd). */
  issueDateTo?: string;
  /** When true, invoices with a $0 total are excluded. */
  excludeZeroTotal?: boolean;
  /** When true, only invoices with a $0 total are included. */
  zeroTotalOnly?: boolean;
  /** When true, only invoices flagged as needing changes are included. */
  needsChange?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const invoiceKeys = {
  all: ['invoices'] as const,
  list: (params: InvoiceListParams) => ['invoices', 'list', params] as const,
  totals: (params: InvoiceTotalsParams) => ['invoices', 'totals', params] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
  preview: (
    source: InvoiceGenerationSource,
    thruDate: string,
    fromDate: string,
    includeBilledExternally: boolean,
  ) => ['invoices', 'preview', source, thruDate, fromDate, includeBilledExternally] as const,
  previewEntries: (
    source: InvoiceGenerationSource,
    key: string,
    thruDate: string,
    fromDate: string,
    includeBilledExternally: boolean,
  ) =>
    [
      'invoices',
      'preview',
      source,
      'entries',
      key,
      thruDate,
      fromDate,
      includeBilledExternally,
    ] as const,
  timeEntries: (id: string) => ['invoices', 'detail', id, 'time-entries'] as const,
};

export function useInvoices(params: InvoiceListParams): UseQueryResult<PaginatedResult<Invoice>> {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Invoice>>(
        `/invoices${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          customerId: params.customerId,
          agreementId: params.agreementId,
          projectId: params.projectId,
          activeReviewerId: params.activeReviewerId,
          activeReviewerIds: params.activeReviewerIds,
          source: params.source,
          status: params.status,
          statuses: params.statuses?.length ? params.statuses.join(',') : undefined,
          issueDateFrom: params.issueDateFrom,
          issueDateTo: params.issueDateTo,
          excludeZeroTotal: params.excludeZeroTotal ? true : undefined,
          zeroTotalOnly: params.zeroTotalOnly ? true : undefined,
          needsChange: params.needsChange ? true : undefined,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export type InvoiceTotalsParams = Omit<InvoiceListParams, 'page' | 'pageSize'>;

/** Revenue totals for every invoice matching the filters (all pages), split by source. */
export function useInvoiceTotals(params: InvoiceTotalsParams): UseQueryResult<InvoiceTotals> {
  return useQuery({
    queryKey: invoiceKeys.totals(params),
    queryFn: () =>
      api.get<InvoiceTotals>(
        `/invoices/totals${buildQuery({
          search: params.search,
          customerId: params.customerId,
          agreementId: params.agreementId,
          projectId: params.projectId,
          activeReviewerId: params.activeReviewerId,
          activeReviewerIds: params.activeReviewerIds,
          source: params.source,
          status: params.status,
          statuses: params.statuses?.length ? params.statuses.join(',') : undefined,
          issueDateFrom: params.issueDateFrom,
          issueDateTo: params.issueDateTo,
          excludeZeroTotal: params.excludeZeroTotal ? true : undefined,
          zeroTotalOnly: params.zeroTotalOnly ? true : undefined,
          needsChange: params.needsChange ? true : undefined,
        })}`,
      ),
  });
}

export function useInvoice(id: string): UseQueryResult<Invoice> {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () => api.get<Invoice>(`/invoices/${id}`),
  });
}

export function usePreviewInvoices(
  source: InvoiceGenerationSource,
  thruDate: string,
  fromDate: string,
  includeBilledExternally: boolean,
  enabled: boolean,
): UseQueryResult<InvoicePreview[]> {
  return useQuery({
    queryKey: invoiceKeys.preview(source, thruDate, fromDate, includeBilledExternally),
    enabled: enabled && Boolean(thruDate),
    queryFn: () =>
      api.get<InvoicePreview[]>(
        `/invoices/preview/${source}${buildQuery({
          thruDate,
          fromDate: fromDate || undefined,
          includeBilledExternally: includeBilledExternally ? true : undefined,
        })}`,
      ),
  });
}

/** Fetches the billable time entries that would land on a project/tickets invoice. */
export function usePreviewInvoiceEntries(
  source: InvoiceGenerationSource,
  key: string | null,
  thruDate: string,
  fromDate: string,
  includeBilledExternally: boolean,
  enabled: boolean,
): UseQueryResult<BillingAuditTimeEntry[]> {
  return useQuery({
    queryKey: invoiceKeys.previewEntries(
      source,
      key ?? '',
      thruDate,
      fromDate,
      includeBilledExternally,
    ),
    enabled: enabled && Boolean(key) && Boolean(thruDate),
    queryFn: () =>
      api.get<BillingAuditTimeEntry[]>(
        `/invoices/preview/${source}/entries/${key ?? ''}${buildQuery({
          thruDate,
          fromDate: fromDate || undefined,
          includeBilledExternally: includeBilledExternally ? true : undefined,
        })}`,
      ),
  });
}

/** Fetches the time entries linked to an existing invoice (for the draft-invoice view). */
export function useInvoiceTimeEntries(
  id: string,
  enabled: boolean,
): UseQueryResult<BillingAuditTimeEntry[]> {
  return useQuery({
    queryKey: invoiceKeys.timeEntries(id),
    enabled: enabled && Boolean(id),
    queryFn: () => api.get<BillingAuditTimeEntry[]>(`/invoices/${id}/time-entries`),
  });
}

export interface WriteOffTimeEntryArgs {
  timeEntryId: string;
  input: WriteOffTimeEntryInput;
}

/** Write off (or restore) a single time entry; the caller invalidates affected views. */
export function useWriteOffTimeEntry(): UseMutationResult<
  BillingAuditTimeEntry,
  unknown,
  WriteOffTimeEntryArgs
> {
  return useMutation({
    mutationFn: ({ timeEntryId, input }: WriteOffTimeEntryArgs) =>
      api.post<BillingAuditTimeEntry>(`/invoices/time-entries/${timeEntryId}/write-off`, input),
  });
}

/** Recompute resolved rates for a set of time entries; the caller invalidates views. */
export function useRecomputeEntryRates(): UseMutationResult<
  RecomputeRatesResult,
  unknown,
  string[]
> {
  return useMutation({
    mutationFn: (timeEntryIds: string[]) =>
      api.post<RecomputeRatesResult>('/invoices/time-entries/recompute-rates', { timeEntryIds }),
  });
}

export interface GenerateInvoicesArgs {
  source: InvoiceGenerationSource;
  input: GenerateInvoicesInput;
}

export function useGenerateInvoices(): UseMutationResult<Invoice[], unknown, GenerateInvoicesArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ source, input }: GenerateInvoicesArgs) =>
      api.post<Invoice[]>(`/invoices/generate/${source}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** Preview the one-off prorated invoice for an agreement (first cycle / new lines). */
export function useProrateInvoicePreview(
  agreementId: string,
  enabled = true,
): UseQueryResult<ProrateInvoicePreview> {
  return useQuery({
    queryKey: ['invoices', 'prorate-preview', agreementId],
    enabled: enabled && Boolean(agreementId),
    queryFn: () => api.get<ProrateInvoicePreview>(`/invoices/prorate-preview/${agreementId}`),
  });
}

export interface GenerateProrateArgs {
  agreementId: string;
  input: GenerateProrateInput;
}

/** Generate the prorated draft invoice for an agreement. */
export function useGenerateProrateInvoice(): UseMutationResult<
  Invoice,
  unknown,
  GenerateProrateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, input }: GenerateProrateArgs) =>
      api.post<Invoice>(`/invoices/prorate/${agreementId}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
}

export interface UpdateInvoiceArgs {
  id: string;
  input: UpdateInvoiceInput;
}

/** Create an ad-hoc invoice or credit memo with manual line items. */
export function useCreateInvoice(): UseMutationResult<Invoice, unknown, CreateInvoiceInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) => api.post<Invoice>('/invoices', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

export function useUpdateInvoice(): UseMutationResult<Invoice, unknown, UpdateInvoiceArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateInvoiceArgs) => api.patch<Invoice>(`/invoices/${id}`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Invalidations shared by the per-line edit mutations (detail, lists, QBO mapping). */
function invalidateAfterLineEdit(queryClient: ReturnType<typeof useQueryClient>, id: string): void {
  void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
  void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
  void queryClient.invalidateQueries({ queryKey: ['qbo'] });
}

export interface AddInvoiceLineArgs {
  id: string;
  input: CreateInvoiceLineInput;
}

/** Add an ad-hoc (fee) line — e.g. a discount or extra charge — to a draft invoice. */
export function useAddInvoiceLine(): UseMutationResult<Invoice, unknown, AddInvoiceLineArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: AddInvoiceLineArgs) =>
      api.post<Invoice>(`/invoices/${id}/lines`, input),
    onSuccess: (_data, { id }) => invalidateAfterLineEdit(queryClient, id),
  });
}

export interface UpdateInvoiceLineArgs {
  id: string;
  lineId: string;
  input: UpdateInvoiceLineInput;
}

/** Edit a single line (description/quantity/rate/product) on a draft invoice. */
export function useUpdateInvoiceLine(): UseMutationResult<Invoice, unknown, UpdateInvoiceLineArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lineId, input }: UpdateInvoiceLineArgs) =>
      api.patch<Invoice>(`/invoices/${id}/lines/${lineId}`, input),
    onSuccess: (_data, { id }) => invalidateAfterLineEdit(queryClient, id),
  });
}

export interface RemoveInvoiceLineArgs {
  id: string;
  lineId: string;
}

/** Remove a fee/subscription line from a draft invoice (subscription rolls billed-through back). */
export function useRemoveInvoiceLine(): UseMutationResult<Invoice, unknown, RemoveInvoiceLineArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lineId }: RemoveInvoiceLineArgs) =>
      api.delete<Invoice>(`/invoices/${id}/lines/${lineId}`),
    onSuccess: (_data, { id }) => invalidateAfterLineEdit(queryClient, id),
  });
}

export interface ReorderInvoiceLinesArgs {
  id: string;
  /** Every line id on the invoice, in the desired top-to-bottom order. */
  orderedIds: string[];
}

/** Reorder a draft invoice's lines; the document, PDF, and QBO push all follow this order. */
export function useReorderInvoiceLines(): UseMutationResult<
  Invoice,
  unknown,
  ReorderInvoiceLinesArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orderedIds }: ReorderInvoiceLinesArgs) =>
      api.patch<Invoice>(`/invoices/${id}/lines/reorder`, { orderedIds }),
    onSuccess: (_data, { id }) => invalidateAfterLineEdit(queryClient, id),
  });
}

/** Restamp a draft/in-review invoice's time lines with the current QBO SKU/class config. */
export function useRefreshQboMapping(): UseMutationResult<
  RefreshQboMappingResult,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<RefreshQboMappingResult>(`/invoices/${id}/refresh-qbo-mapping`),
    onSuccess: (_data, id) => invalidateAfterLineEdit(queryClient, id),
  });
}

export interface SendInvoiceArgs {
  id: string;
  input: SendInvoiceInput;
}

/** Email the invoice to the customer and mark it sent. */
export function useSendInvoice(): UseMutationResult<Invoice, unknown, SendInvoiceArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendInvoiceArgs) =>
      api.post<Invoice>(`/invoices/${id}/send`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export interface SendToReviewArgs {
  id: string;
  input: SendToReviewInput;
}

/** Assign an active reviewer and move the invoice into review. */
export function useSendToReview(): UseMutationResult<Invoice, unknown, SendToReviewArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendToReviewArgs) =>
      api.post<Invoice>(`/invoices/${id}/send-to-review`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Members who may review invoices (Generate Invoices access, or admins). */
export function useInvoiceReviewers(): UseQueryResult<InvoiceReviewerOption[]> {
  return useQuery({
    queryKey: ['invoices', 'reviewers'] as const,
    queryFn: () => api.get<InvoiceReviewerOption[]>('/invoices/reviewers'),
  });
}

/** Bulk-assign a reviewer to several invoices (silent; no change request or email). */
export function useBulkSendToReview(): UseMutationResult<
  BulkSendToReviewResult,
  unknown,
  BulkSendToReviewInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkSendToReviewInput) =>
      api.post<BulkSendToReviewResult>('/invoices/send-to-review-bulk', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** Bulk-change the invoice date on several invoices (due dates are recalculated). */
export function useBulkSetInvoiceDate(): UseMutationResult<
  BulkSetInvoiceDateResult,
  unknown,
  BulkSetInvoiceDateInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkSetInvoiceDateInput) =>
      api.post<BulkSetInvoiceDateResult>('/invoices/set-invoice-date-bulk', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

export interface RequestInvoiceChangesArgs {
  id: string;
  input: RequestInvoiceChangesInput;
}

/** Flag an invoice as needing changes, route it to a reviewer, and email them. */
export function useRequestInvoiceChanges(): UseMutationResult<
  Invoice,
  unknown,
  RequestInvoiceChangesArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: RequestInvoiceChangesArgs) =>
      api.post<Invoice>(`/invoices/${id}/request-changes`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Clear an invoice's "needs change" flag. */
export function useClearNeedsChange(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/clear-needs-change`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Park an invoice on hold — it cannot be emailed or pushed to QBO until released. */
export function useHoldInvoice(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/hold`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Release a held invoice back to draft. */
export function useReleaseInvoiceHold(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/release-hold`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Clear the billing reminder on the invoice's customer (affects all of that customer's invoices). */
export function useClearCustomerBillingReminder(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/clear-billing-reminder`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/** Finalize an invoice without sending it. */
export function useCloseInvoice(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/close`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Reopen a sent or closed invoice back to draft so it can be edited and resent. */
export function useReopenInvoice(): UseMutationResult<Invoice, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Invoice>(`/invoices/${id}/reopen`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

/** Push a sent invoice to QuickBooks Online. */
export function useSyncQboInvoice(): UseMutationResult<QboSyncResult, unknown, string> {
  return useMutation({
    mutationFn: (id: string) => api.post<QboSyncResult>(`/qbo/invoices/${id}/sync`),
  });
}

/** Batch-push selected draft/in-review invoices to QBO, optionally emailing each. */
export function useQboBatch(): UseMutationResult<QboBatchResult, unknown, QboBatchActionInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: QboBatchActionInput) =>
      api.post<QboBatchResult>('/qbo/invoices/batch', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

export interface VoidInvoiceArgs {
  id: string;
  input: VoidInvoiceInput;
}

export function useVoidInvoice(): UseMutationResult<Invoice, unknown, VoidInvoiceArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: VoidInvoiceArgs) =>
      api.post<Invoice>(`/invoices/${id}/void`, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
  });
}

export function useDeleteInvoice(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/invoices/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** Delete a draft but keep its billing cursors advanced and mark covered time billed elsewhere. */
export function useMarkBilledAndRemoveInvoice(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/invoices/${id}/mark-billed`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
}

/** Open the invoice PDF in a new browser tab for preview. */
export function usePreviewInvoicePdf(): UseMutationResult<DownloadResult, unknown, Invoice> {
  return useMutation({
    mutationFn: (invoice: Invoice) => api.download(`/invoices/${invoice.id}/pdf`),
    onSuccess: (result) => {
      const url = URL.createObjectURL(result.blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the opened tab has time to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
}
